export type FrameGeo = {
  nw: number;
  nh: number;
  /** janela vazia da moldura, em pixels da imagem original */
  wl: number;
  wr: number;
  wt: number;
  wb: number;
};

const cache = new Map<string, Promise<FrameGeo>>();

/** Sem como medir: assume uma margem de 12% em cada lado */
function fallback(nw: number, nh: number): FrameGeo {
  return { nw, nh, wl: nw * 0.12, wr: nw * 0.88, wt: nh * 0.12, wb: nh * 0.88 };
}

const median = (a: number[]) => [...a].sort((x, y) => x - y)[a.length >> 1];

/**
 * Descobre o buraco central da moldura lendo o canal alfa. Parte do centro e
 * anda ate achar pixel opaco; usa a mediana de varias linhas/colunas para
 * ignorar espinhos que avancam para dentro.
 */
function measure(img: HTMLImageElement): FrameGeo {
  const nw = img.naturalWidth || 1200;
  const nh = img.naturalHeight || 1600;
  try {
    const k = Math.min(1, 400 / Math.max(nw, nh));
    const w = Math.max(16, Math.round(nw * k));
    const h = Math.max(16, Math.round(nh * k));
    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;
    const g = c.getContext("2d", { willReadFrequently: true });
    if (!g) return fallback(nw, nh);
    g.drawImage(img, 0, 0, w, h);
    const d = g.getImageData(0, 0, w, h).data;
    const solid = (x: number, y: number) => d[(y * w + x) * 4 + 3] > 40;
    const cx = w >> 1;
    const cy = h >> 1;
    if (solid(cx, cy)) return fallback(nw, nh);

    const L: number[] = [];
    const R: number[] = [];
    const T: number[] = [];
    const B: number[] = [];
    for (let i = 0; i <= 20; i++) {
      const y = Math.round(h * (0.35 + (0.3 * i) / 20));
      let x = cx;
      while (x > 0 && !solid(x, y)) x--;
      L.push(x);
      x = cx;
      while (x < w - 1 && !solid(x, y)) x++;
      R.push(x);

      const xx = Math.round(w * (0.35 + (0.3 * i) / 20));
      let yy = cy;
      while (yy > 0 && !solid(xx, yy)) yy--;
      T.push(yy);
      yy = cy;
      while (yy < h - 1 && !solid(xx, yy)) yy++;
      B.push(yy);
    }
    const wl = ((median(L) + 1) / w) * nw;
    const wr = (median(R) / w) * nw;
    const wt = ((median(T) + 1) / h) * nh;
    const wb = (median(B) / h) * nh;
    if (wr - wl < nw * 0.2 || wb - wt < nh * 0.2) return fallback(nw, nh);
    return { nw, nh, wl, wr, wt, wb };
  } catch {
    return fallback(nw, nh);
  }
}

export function frameGeo(url: string): Promise<FrameGeo> {
  let p = cache.get(url);
  if (!p) {
    p = new Promise<FrameGeo>((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(measure(img));
      img.onerror = () => resolve(fallback(1200, 1600));
      img.src = url;
    });
    cache.set(url, p);
  }
  return p;
}
